import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  useParams,
} from "react-router";
import { Box } from "@mui/material";
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import SigninCallbackPage from "./auth/pages/SigninCallbackPage";
import SignoutCallbackPage from "./auth/pages/SignoutCallbackPage";
import SilentCallbackPage from "./auth/pages/SilentCallbackPage";
import AutoLoginPage from "./auth/pages/AutoLoginPage";
import SessionExpiredModal from "./auth/components/SessionExpiredModal";
import { colors } from "./styles/theme";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { de } from "date-fns/locale";
import { AuthProvider, useAuth } from "./auth/AuthProvider";
import LoadingState from "./components/states/LoadingState";
import { ToastProvider } from "./contexts/ToastContext";
import { HeaderProvider } from "./contexts/HeaderContext";
import ErrorBoundary from "./components/common/feedback/ErrorBoundary";

import { Windpark } from "./types";
import OverviewPage from "./components/pages/overview/OverviewPage";
import AdminOverviewPage from "./components/pages/overviewAdmin/AdminOverviewPage";
import AdminOrgDetailPage from "./components/pages/overviewAdmin/AdminOrgDetailPage";
import AccessDeniedPage from "./components/pages/AccessDeniedPage";
import ProjectLayout from "./components/layout/ProjectLayout";
import ScrollToTop from "./components/common/misc/ScrollToTop";
import { apiService } from "./services/api";

const ADMIN_PERMISSION = "admin:write";
const ORGANISATION_PERMISSION = "organisation:write";

/**
 * Guard für die Admin-Übersicht (alle Organisationen) – nur globale Admins.
 */
const AdminGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  if (!user?.permissions.includes(ADMIN_PERMISSION)) {
    return (
      <AccessDeniedPage
        requiredPermission={ADMIN_PERMISSION}
        pageName="Admin-Bereich"
      />
    );
  }
  return <>{children}</>;
};

/**
 * Guard für die Organisations-Detailseite – globale Admins dürfen jede Organisation
 * sehen, Organisations-Admins (`organisation:write`) nur ihre eigene (per orgId aus der URL).
 */
const OrgAdminGuard: React.FC<{
  orgId?: string;
  children: React.ReactNode;
}> = ({ orgId, children }) => {
  const { user } = useAuth();
  const isGlobalAdmin = user?.permissions.includes(ADMIN_PERMISSION);
  const isOwnOrgAdmin =
    user?.permissions.includes(ORGANISATION_PERMISSION) &&
    orgId === user?.organisation_id;
  if (!isGlobalAdmin && !isOwnOrgAdmin) {
    return (
      <AccessDeniedPage
        requiredPermission={ORGANISATION_PERMISSION}
        pageName="Organisation verwalten"
      />
    );
  }
  return <>{children}</>;
};

/**
 * Liest `orgId` aus der URL und reicht sie an den OrgAdminGuard weiter.
 */
const AdminOrgDetailRoute: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>();
  return (
    <OrgAdminGuard orgId={orgId}>
      <AdminOrgDetailPage />
    </OrgAdminGuard>
  );
};

/**
 * Pfade, die ohne Authentifizierung erreichbar sein müssen:
 * - `/signin-callback` / `/signout-callback`: Auth0 Redirect-URIs für Code-Tausch & Logout
 * - `/silent-callback`: Auth0 Redirect-URI für den Silent-Renew-iFrame (Token-Refresh im Hintergrund)
 * - `/login`: Einstiegspunkt für Auth0-Invitation-Links (verwendet ebenfalls AutoLoginPage)
 */
const PUBLIC_PATHS = [
  "/signin-callback",
  "/signout-callback",
  "/silent-callback",
  "/login",
];

const AppContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [currentProject, setCurrentProject] = useState<Windpark | null>(null);

  const { isAuthenticated, isLoading, sessionExpired, login } = useAuth();

  // ProjectId aus URL extrahieren (ändert sich nur bei Projekt-Wechsel, nicht bei Tab-Wechsel)
  const projectIdFromUrl =
    location.pathname.match(/^\/project\/([^\/]+)/)?.[1] || null;

  // Lade aktuelles Projekt nur bei Wechsel der projectId
  useEffect(() => {
    const loadCurrentProject = async () => {
      if (projectIdFromUrl) {
        // Nur laden, wenn sich die projectId tatsächlich geändert hat
        if (currentProject?.id === projectIdFromUrl) return;

        try {
          const project = await apiService.getProject(projectIdFromUrl);
          setCurrentProject(project);
        } catch (error) {
          // Bei Fehler zurück zur Übersicht
          navigate("/");
        }
      } else {
        // Wenn wir NICHT in einem Projekt-Kontext sind, setze currentProject zurück
        setCurrentProject((prev) => {
          if (prev !== null) {
            return null;
          }
          return prev;
        });
      }
    };

    if (isAuthenticated) {
      loadCurrentProject();
    }
  }, [projectIdFromUrl, isAuthenticated, navigate]);

  /**
   * Fügt ein neues Windpark-Projekt hinzu und navigiert zur Detailseite
   */
  const handleNewProject = (newWindpark: Windpark) => {
    setCurrentProject(newWindpark);
    navigate(`/project/${newWindpark.id}/overview`);
  };

  /**
   * Navigiert zurück zur Übersichtsseite
   */
  const handleBackToOverview = () => {
    navigate("/");
  };

  /**
   * Navigiert zurück zur Projekt-Detailseite
   */
  const handleBackToProjectDetail = () => {
    if (currentProject) {
      navigate(`/project/${currentProject.id}/overview`);
    }
  };

  /**
   * Lädt das aktuelle Projekt neu aus dem Backend.
   * Wird aufgerufen, wenn sich Projekt-Daten geändert haben (z.B. aktiver Ertragswert).
   */
  const refreshProject = async () => {
    if (!currentProject?.id) return;
    try {
      const project = await apiService.getProject(currentProject.id);
      setCurrentProject(project);
    } catch (error) {}
  };

  // Nach einem Projektwechsel steht bis zur Antwort noch das vorige Projekt im State. Es darf
  // weder im Header noch auf den Seiten erscheinen – sonst wirkten Aktionen wie Löschen auf
  // das falsche Projekt.
  const loadedProject =
    currentProject && currentProject.id === projectIdFromUrl
      ? currentProject
      : null;

  const isPublicPath = PUBLIC_PATHS.includes(location.pathname);

  if (isPublicPath) {
    return (
      <Routes>
        <Route path="/signin-callback" element={<SigninCallbackPage />} />
        <Route path="/signout-callback" element={<SignoutCallbackPage />} />
        <Route path="/silent-callback" element={<SilentCallbackPage />} />
        <Route path="/login" element={<AutoLoginPage />} />
      </Routes>
    );
  }

  if (isLoading) {
    return (
      <LoadingState
        fullScreen={true}
        message="Authentifizierung..."
        subtitle="Bitte warten Sie einen Moment."
      />
    );
  }

  if (!isAuthenticated) {
    return <AutoLoginPage />;
  }

  // Zeige Hauptanwendung wenn authentifiziert
  return (
    <>
      <ScrollToTop />
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          backgroundColor: colors.background,
        }}
      >
        <Header project={loadedProject ?? undefined} />

        <Box component="main" sx={{ flex: 1, py: 3 }}>
          <Routes>
            <Route
              path="/"
              element={<OverviewPage onProjectCreated={handleNewProject} />}
            />
            <Route
              path="/project/:projectId/*"
              element={
                loadedProject ? (
                  <ProjectLayout
                    projectName={loadedProject.name}
                    currentProject={loadedProject}
                    onBack={handleBackToOverview}
                    onProjectUpdated={refreshProject}
                  />
                ) : (
                  <LoadingState
                    fullScreen={true}
                    message="Lade Projekt..."
                    subtitle="Bitte warten Sie einen Moment."
                  />
                )
              }
            />
            <Route
              path="/admin"
              element={
                <AdminGuard>
                  <AdminOverviewPage />
                </AdminGuard>
              }
            />
            <Route
              path="/admin/organisations/:orgId/*"
              element={<AdminOrgDetailRoute />}
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Box>

        <Footer />
      </Box>

      <SessionExpiredModal open={sessionExpired} onLogin={() => login()} />
    </>
  );
};

/**
 * Wrapper-Komponente mit AuthProvider, ToastProvider und Router
 */
const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
          <HeaderProvider>
            <ToastProvider>
              <Router>
                <AppContent />
              </Router>
            </ToastProvider>
          </HeaderProvider>
        </LocalizationProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
